import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuthUserContext } from '../../providers';
import { DonorStd } from '../../models/DonorStd';
import { DonorSemen } from '../../models/DonorSemen';
import Spinner from '../../components/Spinner';
import { useTitle } from '../../hooks/useTitle';
import { useScrollToAnchor } from '../../hooks/useScrollToAnchor';

const MypageCheckup = () => {
  const authUser = useAuthUserContext().user;
  const { get, post, loading } = useApi();
  const [std, setStd] = useState<DonorStd | null>(null);
  const [semen, setSemen] = useState<DonorSemen | null>(null);
  const [course, setCourse] = useState<'std' | 'semen' | 'both' | null>(null);
  const { scrollToAnchor } = useScrollToAnchor();

  useTitle('Ton Bébé | 検査結果登録');

  useEffect(() => {
    scrollToAnchor();
  }, [scrollToAnchor]);

  useEffect(() => {
    get({
      endpoint: `api/v1/donors/${ authUser?.userId }/show_std_semen`,
      onSuccess: (res) => {
        setStd(res.data.std.id ? new DonorStd(res.data.std) : null);
        setSemen(res.data.semen.id ? new DonorSemen(res.data.semen) : null);
      },
      onError: (err) => {
        toast.error("情報の取得に失敗しました。時間をおいてお試しください。");
      },
    });
  }, [get, authUser?.userId]);

  useEffect(() => {
    const stdValid = std ? std.isValidForRegistration() : false;
    const semenValid = semen ? semen.isValidForRegistration() : false;
    if (stdValid && semenValid) {
      setCourse('both');
    } else if (stdValid) {
      setCourse('std');
    } else if (semenValid) {
      setCourse('semen');
    } else {
      setCourse(null);
    }
  }, [std, semen]);

  const renderStdRow = () => {
    return (
      <>
        <td className={ std?.totalAlert ? 'txt_pink' : '' }>{ std?.totalText }</td>
        <td className={ std?.fileAlert ? 'txt_pink' : '' }>{ std?.fileText }</td>
        <td className={ std?.statusAlert ? 'txt_pink' : '' }>{ std?.statusText }</td>
      </>
    );
  };

  const renderSemenRow = () => {
    return (
      <>
        <td>{ semen?.total }</td>
        <td className={ semen?.fileAlert ? 'txt_pink' : '' }>{ semen?.fileText }</td>
        <td className={ semen?.statusAlert ? 'txt_pink' : '' }>{ semen?.statusText }</td>
      </>
    );
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCourse(event.target.value as 'std' | 'semen' | 'both');
  };

  const handleRegisterClick = () => {
    if (!course) {
      toast.error("コースを選択してください。");
      return;
    }

    if (course === 'std' || course === 'both') {
      if (std?.status === 'SUBMITTED') {
        toast.error("性病検査は申請中のため、登録処理を進めることはできません。");
        return;
      }

      if (std?.status === 'CERTIFIED' || std?.status === 'REMANDED') {
        toast.error("性病検査は登録・入金手続き済みです。「編集」ボタンよりお進みください。");
        return;
      }

      if (!std || !std.isValidForRegistration()) {
        toast.error("性病検査の入力内容を確認してください。全ての必須項目及び書類の提出が必要です（６ヶ月以内のみ有効）");
        return;
      }
    }

    if (course === 'semen' || course === 'both') {
      if (semen?.status === 'SUBMITTED') {
        toast.error("精液検査は申請中のため、登録処理を進めることはできません。");
        return;
      }

      if (semen?.status === 'CERTIFIED' || semen?.status === 'REMANDED') {
        toast.error("精液検査は登録・入金手続き済みです。「編集」ボタンよりお進みください。");
        return;
      }

      if (!semen || !semen.isValidForRegistration()) {
        toast.error("精液検査の入力内容を確認してください。全ての必須項目及び書類の提出が必要です（６ヶ月以内のみ有効）");
        return;
      }
    }

    post({
      endpoint: '/api/v1/create_checkout_session',
      body: {
        course: course,
        success_path: `/mypage/checkups/complete?status=succeeded&session_id={CHECKOUT_SESSION_ID}`,
        cancel_path: `/mypage/checkups/complete?status=canceled&session_id={CHECKOUT_SESSION_ID}`,
      },
      onSuccess: (res) => {
        if (res?.url) {
          window.location.href = res.url;
        } else {
          toast.error("決済ページへの遷移に失敗しました。時間をおいてお試しください。");
        }
      },
      onError: (err) => {
        toast.error("登録処理中にエラーが発生しました。時間をおいてお試しください。");
      },
    });
  };

  return (
    <main className="sub">
      <section className="donor_std_semen">
        <h2 className="main_title"><span>検査結果登録</span></h2>
        <h3 className="sub_title">性病検査・精液検査について</h3>
        <div className="text_list">
          <p>
            提供を受ける方が安心してドナーを探せるよう、ドナーの方には性病検査および精液検査の結果をご登録いただくようお願いしております。<br/>ご登録の際に検査結果書類の画像（検査結果証もしくはウェブ画面のスクリーンショット）を合わせてご提出いただき、運営事務局にて内容を確認いたします。
          </p>
          <p>確認が取れた後、他のユーザーが検査結果の値を閲覧できるようになります。<br/>（書類の画像が公開されることはありません。）</p>
          <div className="kit_cards">
            <div className="kit_card">
              <img
                src="//thumbnail.image.rakuten.co.jp/@0_gold/std-checker/img/type_Em_01.png"
                alt="性病検査キット"
                className="kit_card_img"
              />
              <div className="kit_card_body">
                <p className="kit_card_name">性病検査キット</p>
                <p className="kit_card_desc">HIV・梅毒・クラミジア・淋菌など主要な性病を自宅で検査できます。</p>
                <div className="kit_card_btnarea">
                  <a
                    href="//af.moshimo.com/af/c/click?a_id=1784748&p_id=54&pc_id=54&pl_id=616&url=https%3A%2F%2Fitem.rakuten.co.jp%2Fstd-checker%2Ftype_em%2F&m=http%3A%2F%2Fm.rakuten.co.jp%2Fstd-checker%2Fi%2F10000007%2F"
                    rel="nofollow"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="kit_shop_btn rakuten"
                  >楽天市場で購入</a>
                  <a
                    href="//af.moshimo.com/af/c/click?a_id=1784765&p_id=170&pc_id=185&pl_id=4062&url=https%3A%2F%2Fwww.amazon.co.jp%2Fdp%2FB00JP4EFWG"
                    rel="nofollow"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="kit_shop_btn amazon"
                  >Amazonで購入</a>
                </div>
              </div>
            </div>
            <div className="kit_card">
              <img
                src="//thumbnail.image.rakuten.co.jp/@0_mall/onlinekensaoes/cabinet/akh.jpg"
                alt="精液検査キット"
                className="kit_card_img"
              />
              <div className="kit_card_body">
                <p className="kit_card_name">精液検査キット</p>
                <p className="kit_card_desc">精子濃度・運動率などを自宅で検査できます。</p>
                <div className="kit_card_btnarea">
                  <a
                    href="//af.moshimo.com/af/c/click?a_id=1784748&p_id=54&pc_id=54&pl_id=616&url=https%3A%2F%2Fitem.rakuten.co.jp%2Fonlinekensaoes%2F0001%2F&m=http%3A%2F%2Fm.rakuten.co.jp%2Fonlinekensaoes%2Fi%2F10000000%2F"
                    rel="nofollow"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="kit_shop_btn rakuten"
                  >楽天市場で購入</a>
                  <a
                    href="//af.moshimo.com/af/c/click?a_id=1784765&p_id=170&pc_id=185&pl_id=4062&url=https%3A%2F%2Fwww.amazon.co.jp%2Fdp%2FB00G4UA1PE"
                    rel="nofollow"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="kit_shop_btn amazon"
                  >Amazonで購入</a>
                </div>
              </div>
            </div>
          </div>
          <p>※上記商品はどちらもTon Bébéの入力必須項目を満たしております。</p>
        </div>
        <h3 className="sub_title">検査結果登録申請について</h3>
        <div className="text_list">
          <p>検査結果の確認手数料として性病・精液検査それぞれ3,000円ずつ（両方同時の場合は5,000円）をお支払いただいております。検査結果が正当であると弊社にて審査の後、ドナープロフィールの一部として検査結果が表示されます。
          </p>
          <p>検査結果の有効性は検査日から１年としており、１年以内に再検査の上、再度提出をお願いします。再提出には改めて上記料金が発生します。期限が切れた場合は、検査結果が表示されなくなりますのでご注意ください。</p>
          <p>
            書類の提出後、入金をお願いしております。入金確認後、当サービス運営事務局にて書類内容の確認をいたします。内容に不備がなければ72時間以内に登録内容が公開されます。申請内容に不備があった場合は差し戻しをいたしますので、修正をしてください。
          </p>
          <p>※公開されるのは数値のみです。提出書類の画像が公開されることはありません。<br/>※性病検査は「陽性」があると申請できません。<br/>※支払い方法はクレジットカードのみとなります。</p>
        </div>
        <h3 className="sub_title" id="register_form">検査結果登録</h3>
        <div className="tableform">
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>結果</th>
                  <th>書類添付</th>
                  <th>ステータス</th>
                  <th>編集・更新</th>
                </tr>
              </thead>
              <tbody>
                { loading ? (
                  <tr>
                    <td colSpan={ 5 }>
                      <Spinner centered={ true } />
                    </td>
                  </tr>
                ) : (
                  <>
                    <tr>
                      <th>性病検査</th>
                      { renderStdRow() }
                      <td>
                        { std?.status === 'SUBMITTED' ? (
                          <span>---</span>
                        ) : (
                          <Link to="/mypage/checkups/std" className="bluebtn">編集</Link>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>精液検査</th>
                      { renderSemenRow() }
                      <td>
                        { semen?.status === 'SUBMITTED' ? (
                          <span>---</span>
                        ) : (
                          <Link to="/mypage/checkups/semen" className="bluebtn">編集</Link>
                        )}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="form">
          <span className="wpcf7-form-control-wrap radio-content">
            <span className="wpcf7-form-control wpcf7-radio wpcf7-validates-as-required">
              <span className="wpcf7-list-item first">
                <label>
                  <input
                    type="radio"
                    name="course[]"
                    value="std"
                    onChange={ handleRadioChange }
                    checked={course === 'std'}
                  />
                  <span className="wpcf7-list-item-label">性病検査結果のみ申請する（3,000円）</span>
                </label>
              </span>
              <span className="wpcf7-list-item">
                <label>
                  <input
                    type="radio"
                    name="course[]"
                    value="semen"
                    onChange={ handleRadioChange }
                    checked={course === 'semen'}
                  />
                  <span className="wpcf7-list-item-label">精液検査結果のみ申請する（3,000円）</span>
                </label>
              </span>
              <span className="wpcf7-list-item">
                <label>
                  <input
                    type="radio"
                    name="course[]"
                    value="both"
                    onChange={ handleRadioChange }
                    checked={course === 'both'}
                  />
                  <span className="wpcf7-list-item-label">両方とも申請する（5,000円）</span>
                </label>
              </span>
            </span>
            <p>※全ての必須項目および書類の提出が必要です。（6ヶ月以内のみ有効）</p>
            <button
              className="w_bluebtn"
              disabled={ loading }
              onClick={ handleRegisterClick }
            >
              登録・入金手続きを行う
            </button>
          </span>
        </div>
      </section>
    </main>
  );
}

export default MypageCheckup;
